/**
 * Loading the roster, and turning it into what the draft solver expects.
 *
 * The artifact is fetched once and kept in memory for the session. It is a
 * static file, not part of the bundle -- see scripts/sync-roster.mjs for why.
 */

// Workspace .mjs with JSDoc types and no .d.ts; `allowJs` is what lets tsc
// see through it.
import { decodeRoster, squadsInRange } from '@ipl-sim/data';
import type { DraftPlayer, Role } from '@ipl-sim/engine-cricket';

/** A player as the decoder produces them, before the engine's view is taken. */
interface DecodedPlayer {
  name: string;
  role: Role;
  bat: number;
  pow: number;
  bowl: number;
  posMin: number;
  posMax: number;
  bowlOvers: number;
  canBowl: boolean;
  isFrontlineBowler: boolean;
  isIndian: boolean;
  isOrangeCap: boolean;
  isPurpleCap: boolean;
  year: number;
  city: string;
  id: string;
  cardId: string;
}

/** A drafted or draftable card: the engine's player plus where it came from. */
export interface Card extends DraftPlayer {
  readonly year: number;
  readonly city: string;
  /** "Chennai 2021" -- shown on the card, and a neutral label on purpose. */
  readonly from: string;
}

export interface Squad {
  readonly city: string;
  readonly year: number;
  readonly label: string;
  readonly squad: readonly Card[];
}

/**
 * The decoder writes `id: name`, but the engine's `identityOf` splits on "|"
 * to recover the person behind a season-version. Feeding it the bare name
 * happens to work today (a name with no "|" splits to itself) and would break
 * silently the moment the id format gains a field. Give it the cardId, which
 * is the format it actually documents.
 */
const toCard = (p: DecodedPlayer): Card => ({
  id: p.cardId,
  name: p.name,
  role: p.role,
  bat: p.bat,
  pow: p.pow,
  bowl: p.bowl,
  bowlOvers: p.bowlOvers,
  isIndian: p.isIndian,
  isOrangeCap: p.isOrangeCap,
  isPurpleCap: p.isPurpleCap,
  posMin: p.posMin,
  posMax: p.posMax,
  year: p.year,
  city: p.city,
  from: `${p.city} ${p.year}`,
});

export interface Roster {
  /** Every season present, ascending. Drives the year-range picker. */
  readonly years: readonly number[];
  /** Squads in an inclusive year range -- what the user spins through. */
  squadsIn(from: number, to: number): Squad[];
  /**
   * Every draftable card in a range. The feasibility guard reasons about this,
   * not about the squad on screen: spins are random, so any of these could
   * still be offered.
   */
  poolIn(from: number, to: number): Card[];
}

let cached: Roster | null = null;

export async function loadRoster(signal?: AbortSignal): Promise<Roster> {
  if (cached) return cached;

  const res = await fetch(`${import.meta.env.BASE_URL}roster.json`, { signal });
  if (!res.ok) throw new Error(`roster.json: ${res.status} ${res.statusText}`);

  // decodeRoster throws on a schema mismatch, which is the signal that a
  // service worker is holding an artifact older than this build.
  const decoded = decodeRoster(await res.json());

  // squadsInRange rebuilds its array on every call, and the pool is a flatMap
  // over 166 squads. The draft calls both between taps, so memoise per range.
  const squadCache = new Map<string, Squad[]>();
  const poolCache = new Map<string, Card[]>();

  cached = {
    years: decoded.years,

    squadsIn(from, to) {
      const key = `${from}-${to}`;
      let squads = squadCache.get(key);
      if (!squads) {
        squads = squadsInRange(decoded, from, to).map(
          (t: { city: string; year: number; label: string; squad: DecodedPlayer[] }) => ({
            city: t.city,
            year: t.year,
            label: t.label,
            squad: t.squad.map(toCard),
          }),
        );
        squadCache.set(key, squads!);
      }
      return squads!;
    },

    poolIn(from, to) {
      const key = `${from}-${to}`;
      let pool = poolCache.get(key);
      if (!pool) {
        pool = this.squadsIn(from, to).flatMap((t) => t.squad as Card[]);
        poolCache.set(key, pool);
      }
      return pool;
    },
  };

  return cached;
}
